import { useCallback, useEffect, useMemo, useState } from 'react';

import { ConfirmDialog } from '../components/ConfirmDialog';
import { Drawer } from '../components/Drawer';
import { Modal } from '../components/Modal';
import { emptySourceFormState, SourceForm } from '../components/SourceForm';
import { api, ApiCallError, type FeedSource } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  diffSourceUpdate,
  feedSourceToFormState,
  type SourceFormState,
  sourceFormStateToPayload,
} from '../lib/forms';

/**
 * Sources page — the central tuning surface. Dense table inspired by
 * thesurface.ai's editorial feed view: tight rows, monospace metadata,
 * inline reputation columns.
 *
 * P6.7 (this iteration) wires the create/edit/delete UI on top of P6.5/P6.6.
 *   - Header "+ Add source" → Modal + SourceForm → POST /sources.
 *   - Per-row Edit → Drawer + SourceForm → PUT /sources/{id} (diff-only).
 *   - Per-row Delete → ConfirmDialog → DELETE /sources/{id}.
 */
export function Sources(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.idToken;
  const [sources, setSources] = useState<readonly FeedSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<FeedSource | null>(null);
  const [deleting, setDeleting] = useState<FeedSource | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (idToken === null) return;
      try {
        const result = await api.listSources(idToken, signal);
        setSources(result.sources);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiCallError ? err.message : 'failed to load sources');
        setSources([]);
      }
    },
    [idToken],
  );

  useEffect(() => {
    const ac = new AbortController();
    void reload(ac.signal);
    return () => ac.abort();
  }, [reload]);

  const filtered = useMemo(() => {
    if (sources === null) return null;
    if (filter === '') return sources;
    const q = filter.toLowerCase();
    return sources.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.source_type.toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [sources, filter]);

  const toggleActive = useCallback(
    async (s: FeedSource): Promise<void> => {
      if (idToken === null) return;
      setPendingId(s.id);
      try {
        const result = await api.updateSource(idToken, s.id, { active: !s.active });
        setSources((prev) =>
          prev === null ? prev : prev.map((row) => (row.id === s.id ? result.source : row)),
        );
      } catch (err) {
        setError(err instanceof ApiCallError ? err.message : 'update failed');
      } finally {
        setPendingId(null);
      }
    },
    [idToken],
  );

  const handleCreate = useCallback(
    async (state: SourceFormState): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      setFormError(null);
      try {
        const payload = sourceFormStateToPayload(state);
        const result = await api.createSource(idToken, payload);
        setSources((prev) => (prev === null ? prev : [result.source, ...prev]));
        setCreateOpen(false);
      } catch (err) {
        setFormError(err instanceof ApiCallError ? err.message : 'create failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  const handleUpdate = useCallback(
    async (original: FeedSource, state: SourceFormState): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      setFormError(null);
      try {
        const diff = diffSourceUpdate(original, state);
        if (Object.keys(diff).length === 0) {
          setEditing(null);
          return;
        }
        const result = await api.updateSource(idToken, original.id, diff);
        setSources((prev) =>
          prev === null
            ? prev
            : prev.map((row) => (row.id === original.id ? result.source : row)),
        );
        setEditing(null);
      } catch (err) {
        setFormError(err instanceof ApiCallError ? err.message : 'update failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  const handleDelete = useCallback(
    async (s: FeedSource): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      try {
        await api.deleteSource(idToken, s.id);
        setSources((prev) => (prev === null ? prev : prev.filter((row) => row.id !== s.id)));
        setDeleting(null);
      } catch (err) {
        setError(err instanceof ApiCallError ? err.message : 'delete failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  if (sources === null) {
    return <Loading />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <header className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-base font-mono text-ink">Sources</h1>
          <p className="text-2xs font-mono text-ink-muted uppercase tracking-wider mt-0.5">
            {sources.length} total · {sources.filter((s) => s.active).length} active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="filter…"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); }}
            className="bg-bg-base border border-bg-line rounded px-3 py-1.5 text-sm font-mono w-48 focus:outline-none focus:border-accent"
          />
          <button
            onClick={() => { setCreateOpen(true); setFormError(null); }}
            className="text-2xs font-mono uppercase tracking-wider px-3 py-1.5 rounded bg-accent hover:bg-accent-hover text-white"
          >
            + Add source
          </button>
        </div>
      </header>

      {error !== null && (
        <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded mb-4">
          {error}
        </p>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-2xs font-mono text-ink-muted uppercase tracking-wider border-b border-bg-line">
            <tr>
              <Th>Source</Th>
              <Th>Type</Th>
              <Th>Priority</Th>
              <Th title="Items ingested in last 7d (rolling)" align="right">7d items</Th>
              <Th title="Average quality_score 0-100 across rated items" align="right">Avg Q</Th>
              <Th title="Fraction of last-7d rated items at A-tier" align="right">A-rate</Th>
              <Th align="right">Active</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered !== null && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-ink-muted py-8 font-mono text-2xs">
                  {filter === '' ? 'no sources yet' : `no match for "${filter}"`}
                </td>
              </tr>
            )}
            {filtered?.map((s) => (
              <tr key={s.id} className="border-b border-bg-line/60 row-hover">
                <td className="px-4 py-2.5">
                  <div className="text-ink">{s.name}</div>
                  {s.expertise !== null && (
                    <div className="text-2xs font-mono text-ink-muted truncate max-w-md">
                      {s.expertise}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className="pill-dim">{s.source_type}</span>
                </td>
                <td className="px-4 py-2.5">
                  <PriorityPill priority={s.priority} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {fmtNum(s.rolling_item_count)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {fmtQuality(s.rolling_avg_quality)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {s.rolling_a_rate !== null
                    ? `${(s.rolling_a_rate * 100).toFixed(1)}%`
                    : <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => { void toggleActive(s); }}
                    disabled={pendingId === s.id}
                    className={
                      s.active
                        ? 'pill-ok hover:bg-ok/20 cursor-pointer'
                        : 'pill-dim hover:bg-bg-line cursor-pointer'
                    }
                    title={s.active ? 'click to demote' : 'click to re-activate'}
                  >
                    {pendingId === s.id ? '…' : s.active ? 'on' : 'off'}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => { setEditing(s); setFormError(null); }}
                      className="text-2xs font-mono text-ink-dim hover:text-accent uppercase tracking-wider"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => { setDeleting(s); }}
                      className="text-2xs font-mono text-ink-dim hover:text-bad uppercase tracking-wider"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={createOpen}
        title="Add source"
        onClose={() => { if (!formBusy) setCreateOpen(false); }}
      >
        <SourceForm
          initial={emptySourceFormState()}
          submitLabel="Create"
          busy={formBusy}
          serverError={formError}
          onSubmit={(s) => { void handleCreate(s); }}
          onCancel={() => { setCreateOpen(false); }}
        />
      </Modal>

      <Drawer
        open={editing !== null}
        title={editing !== null ? `Edit · ${editing.name}` : 'Edit'}
        onClose={() => { if (!formBusy) setEditing(null); }}
      >
        {editing !== null && (
          <SourceForm
            initial={feedSourceToFormState(editing)}
            submitLabel="Save"
            busy={formBusy}
            serverError={formError}
            onSubmit={(s) => { void handleUpdate(editing, s); }}
            onCancel={() => { setEditing(null); }}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete source?"
        message={
          deleting !== null
            ? `${deleting.name} — this cascades to all feed_items + digest_items rows.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={formBusy}
        onConfirm={() => { if (deleting !== null) void handleDelete(deleting); }}
        onCancel={() => { setDeleting(null); }}
      />
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center text-2xs font-mono text-ink-muted">
      loading…
    </div>
  );
}

function Th({
  children,
  align,
  title,
}: {
  readonly children: string;
  readonly align?: 'right';
  readonly title?: string;
}): JSX.Element {
  return (
    <th
      title={title}
      className={`px-4 py-2 ${align === 'right' ? 'text-right' : 'text-left'} font-normal`}
    >
      {children}
    </th>
  );
}

function PriorityPill({ priority }: { readonly priority: FeedSource['priority'] }): JSX.Element {
  const cls =
    priority === 'critical' ? 'pill-bad'
    : priority === 'high' ? 'pill-warn'
    : priority === 'normal' ? 'pill-dim'
    : 'pill-dim';
  return <span className={cls}>{priority}</span>;
}

function fmtNum(v: number | null, frac = 0): JSX.Element {
  if (v === null) return <span className="text-ink-muted">—</span>;
  return <span>{v.toFixed(frac)}</span>;
}

/**
 * Quality colour bands — signal-only, no claims about absolute quality.
 *  ≥75 → ok (green)   — solid B+ across the rated 7d window
 *  ≥50 → neutral      — middling; Karpathy lands here under the meaning-weighted prompt
 *  ≥35 → warn         — approaching the auto-demote threshold
 *  <35 → bad          — would be demoted at next reputation run if item count crosses 50
 */
function qualityClass(v: number | null): string {
  if (v === null) return 'text-ink-muted';
  if (v >= 75) return 'text-ok';
  if (v >= 50) return 'text-ink';
  if (v >= 35) return 'text-warn';
  return 'text-bad';
}

function fmtQuality(v: number | null, frac = 1): JSX.Element {
  if (v === null) return <span className="text-ink-muted">—</span>;
  return <span className={qualityClass(v)}>{v.toFixed(frac)}</span>;
}
